import Modal from '@/components/Modal/Modal';
import { FC, useState } from 'react';
import InputSearch from '@/components/InputSearch/InputSearch';
import useSearchUser from '@/services/user/queries/useSearchUser';
import Button, { ButtonVariants } from '@/components/new-design/Button';
import { FriendRequest } from '@/services/friends/friendsServicesTypes';
import { usePostFriendRequest } from '@/services/friends/queries/usePostFriendRequest';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import useGetFriends from '@/services/friends/queries/useGetFriends';
import Avatar from '../../../features/avatar/Avatar';

interface AddFriendModalProps {
  open: boolean;
  onClose: () => void;
  sentRequest: FriendRequest[];
  refetch: () => void;
}

const AddFriendModal: FC<AddFriendModalProps> = ({ open, onClose, sentRequest, refetch }) => {
  const [searchValue, setSearchValue] = useState('');
  const [loadingUserIds, setLoadingUserIds] = useState<string[]>([]);
  const { data: searchedUsers } = useSearchUser({
    alias: searchValue,
  });

  const { data: friendsData } = useGetFriends();

  const user = useSelector((state: ReducerState) => state.auth);

  const { mutate } = usePostFriendRequest();

  const onChangeInputHandler = (value: string) => setSearchValue(value);

  const filteredData = searchedUsers?.data.filter((searchedUser) => searchedUser.id !== user.id);

  const usersFound = filteredData?.length || 0;

  return (
    <Modal
      className="md:min-w-[500px]"
      open={open}
      setClose={onClose}
      titleComponent={
        <Modal.Title title="Add New Friend" onClose={onClose}>
          <InputSearch fullWidth placeholder="Search for a friend..." handleNameSearch={onChangeInputHandler} />
        </Modal.Title>
      }
    >
      <div className="flex flex-col justify-center gap-5"></div>
      {filteredData && (
        <div className="flex flex-col gap-3">
          <span className="text-xxs text-white text-opacity-50">
            {usersFound >= 10 ? 'More than 10 contacts found' : `${usersFound} contacts found`}
          </span>
          {filteredData.map((user) => {
            const invitationAlreadySent =
              sentRequest.find((userInvited) => userInvited.receiverId === user.id) !== undefined;
            const isUserLoading = loadingUserIds.includes(user.id);

            const alreadyFriend = friendsData?.data.find((friend) => friend.id === user.id) !== undefined;

            return (
              <div key={user.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    avatar2D
                    round={false}
                    className="h-10 w-10 rounded-lg border-2 border-ph-primary-violet"
                    userId={user.id}
                  />
                  <span className="text-sm text-white">{user.alias}</span>
                </div>

                <Button
                  disabled={invitationAlreadySent || isUserLoading || alreadyFriend}
                  onClick={() => {
                    setLoadingUserIds((prev) => [...prev, user.id]);
                    mutate({ userId: user.id });
                    refetch();
                  }}
                  variant={ButtonVariants.primary}
                  className="min-w-36"
                >
                  {alreadyFriend
                    ? 'Is Your Friend'
                    : invitationAlreadySent || isUserLoading
                      ? 'Request Sent'
                      : 'Send Request'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default AddFriendModal;
